import {
  type BoundingBox2D,
  type BoundingBoxFileData,
} from '@wandb/weave/common/types/media';
import {BoundingBoxesCanvas} from '@wandb/weave/components/Panel2/ImageWithOverlays';
import Color from 'color';
import * as _ from 'lodash';
import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {clearBoxData, loadBoxData} from '../../state/media/actions';
import type * as PanelTypes from '../../state/views/panel/types';
import {type PartRefFromObjSchema} from '../../state/views/types';
import {type RunSignature} from '../../types/run';
import {boxColor, type WBFile} from '../../util/media';
import {type FileInfo, useLoadFile} from '../../util/requests';
import {
  type BoundingBoxClassControl,
  type BoundingBoxSliderControl,
  type Style,
} from '../MediaCard';

type BoundingBoxesProps = {
  style?: React.CSSProperties;
  mediaKey: string;
  boxKey: string;
  boxFileInfo: WBFile;
  runSignature: RunSignature;
  mediaSize: {
    width: number;
    height: number;
  };
  cardSize: {
    width: number;
    height: number;
  };
  mediaPanelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
  boxStyle?: Style;
  boxToggles?: {
    [classOrAll: string]: BoundingBoxClassControl;
  };
  boxSliders?: {
    [sliderKey: string]: BoundingBoxSliderControl;
  };
};

const NO_BOXES: BoundingBox2D[] = [];

const BoundingBoxesComp = ({
  runSignature,
  boxFileInfo,
  mediaKey,
  mediaPanelRef,
  boxKey,
  cardSize,
  style,
  mediaSize,
  boxStyle,
  boxSliders,
  boxToggles,
}: BoundingBoxesProps) => {
  const dispatch = useDispatch();

  const [boxData, setBoxData] = React.useState<BoundingBoxFileData | null>(
    null
  );
  const [fileMetadata, setFileMetadata] = React.useState<FileInfo | null>(null);
  // Box files without a box_data array still render the image.
  const boxes = Array.isArray(boxData?.box_data) ? boxData.box_data : NO_BOXES;
  const onSuccess = useCallback((b: BoundingBoxFileData, m: FileInfo) => {
    setBoxData(b);
    setFileMetadata(m);
  }, []);

  const classStates = useMemo(() => {
    const classLabels = boxData?.class_labels ?? boxData?.box_data;
    return Object.fromEntries(
      _.map(classLabels, (label, id) => {
        const className =
          typeof label === 'string' ? label : String(label.class_id);
        const color = boxToggles?.[id]?.color;
        if (color) {
          const [r, g, b] = color;
          return [
            id,
            {
              name: className,
              color: Color.rgb(r, g, b).string(),
            },
          ];
        }
        return [id, {name: className, color: boxColor(parseInt(id, 10))}];
      })
    );
  }, [boxData, boxToggles]);

  const classOverlayStates = useMemo(
    () =>
      Object.fromEntries(
        _.map(boxToggles, (controls, id) => [id, {opacity: 1, ...controls}])
      ),
    [boxToggles]
  );

  useLoadFile(runSignature, boxFileInfo.path, {
    onSuccess,
    responseType: 'json',
  });

  useEffect(() => {
    if (fileMetadata == null || boxData == null) {
      return;
    }

    dispatch(
      loadBoxData({
        mediaKey,
        panelID: mediaPanelRef.id,
        // Use the file ID as the identifier instead of
        // combining mediaKey, boxKey, step, index, run.id
        mediaID: fileMetadata.id,
        boxData: boxes,
      })
    );

    return () => {
      if (fileMetadata == null || boxData == null) {
        return;
      }
      dispatch(
        clearBoxData({
          mediaKey,
          panelID: mediaPanelRef.id,
          mediaID: fileMetadata.id,
        })
      );
    };
  }, [boxData, boxes, fileMetadata, mediaPanelRef.id, mediaKey, dispatch]);

  return (
    <div
      data-test="bounding-box"
      data-test-box-sliders={JSON.stringify(boxSliders)}
      data-test-box-toggles={JSON.stringify(boxToggles)}
      data-test-box-style={boxStyle?.lineStyle}
      key={boxKey}
      style={{
        width: '100%',
        height: '100%',
        ...style,
      }}>
      {/* TODO: Don't make the canvas scale make the drawing scale */}
      {boxData && (
        <BoundingBoxesCanvas
          cardSize={cardSize}
          mediaSize={mediaSize}
          boxData={boxes}
          classStates={classStates}
          bboxControls={{
            type: 'box',
            classSearch: '',
            classSetID: '',
            name: '',
            disabled: false,
            classOverlayStates,
            lineStyle: boxStyle?.lineStyle ?? 'line',
          }}
          sliderControls={boxSliders}
        />
      )}
    </div>
  );
};

export const BoundingBoxes = React.memo(BoundingBoxesComp);
